import { useEffect } from 'react';
import { THEME_CATALOG } from '@caro/shared/theme';
import { useApi } from './useApi';
import { api } from './useAuth';
import { logEvent } from '../utils/debugLog';

/**
 * Resident-app theme per site (spec 20260930-caro-editorial-theme, part A, plan §7.9.4, T087).
 *
 * The API resolves site → company → default and delivers the result in the building config the
 * tablet already fetches: `data.building.residentTheme = { key, origin }` (A-FR-4). This hook
 * turns that key into ONE attribute on <html>; the colours and fonts live in static stylesheets
 * (`styles/themes/<key>.css`) keyed on that attribute. Nothing is ever written inline.
 *
 *   null / 'caro-default' → remove `data-caro-theme`, clear the cache, touch nothing else.
 *                            This is the zero-diff path (A-FR-3, AC-A1): no attribute, no inline
 *                            variable, no font download — the tablet looks exactly like before.
 *   known resident theme  → `data-caro-theme="<key>"`, cached in `localStorage.caro_resident_theme`
 *                            so a cold start without network paints the same (index.html preload).
 *   anything else         → as null, plus a warning (A-NFR-4: an unknown key is never silent).
 *
 * `useBranding` is deliberately NOT touched (LD-20): it reads a different, broken path (A-F5).
 * Live changes arrive as `display_settings { refetch: true }` (LD-24); `useMqttCommands` then
 * calls `refetchResidentTheme()` next to the display-defaults refetch.
 */

export const RESIDENT_THEME_STORAGE_KEY = 'caro_resident_theme';
export const RESIDENT_THEME_ATTRIBUTE = 'data-caro-theme';

/** The theme that describes today's look. Never applied as a block. */
const DEFAULT_KEY = 'caro-default';

type ResidentThemeField = { key?: unknown; origin?: unknown } | null | undefined;
type BuildingConfigBody = {
  building?: { residentTheme?: ResidentThemeField } | null;
  data?: { building?: { residentTheme?: ResidentThemeField } | null } | null;
} | null | undefined;

function isResidentTheme(key: string): boolean {
  if (!Object.prototype.hasOwnProperty.call(THEME_CATALOG, key)) return false;
  return THEME_CATALOG[key].apps.includes('resident');
}

function clearCache() {
  try {
    localStorage.removeItem(RESIDENT_THEME_STORAGE_KEY);
  } catch {
    // storage unavailable — nothing cached, nothing to clear
  }
}

/**
 * Apply a resolved theme key to <html>. Returns the key that is now active, or `null` for
 * today's look. Safe to call repeatedly; the null path only removes what a theme added.
 */
export function applyResidentTheme(key: unknown): string | null {
  if (typeof document === 'undefined') return null;
  const root = document.documentElement;

  if (key === null || key === undefined || key === '' || key === DEFAULT_KEY) {
    root.removeAttribute(RESIDENT_THEME_ATTRIBUTE);
    clearCache();
    return null;
  }

  if (typeof key === 'string' && isResidentTheme(key)) {
    root.setAttribute(RESIDENT_THEME_ATTRIBUTE, key);
    try {
      localStorage.setItem(RESIDENT_THEME_STORAGE_KEY, key);
    } catch {
      // storage unavailable — the attribute still applies for this session
    }
    return key;
  }

  // Unknown or not a resident theme (a newer API than this bundle, or a retired key): fall back
  // to today's look and say so. console.warn is mirrored into the persisted debug log.
  console.warn(`[resident-theme] unknown theme key "${String(key)}" — showing the default look`);
  logEvent('resident_theme_unknown_key', { key: String(key) });
  root.removeAttribute(RESIDENT_THEME_ATTRIBUTE);
  clearCache();
  return null;
}

/**
 * The resolved key from a building-config response, whether or not the `{ success, data }`
 * envelope was already unwrapped. `undefined` when the response carries no building at all.
 */
export function readResidentThemeKey(body: BuildingConfigBody): unknown {
  const building = body?.data?.building ?? body?.building;
  if (!building) return undefined;
  const theme = building.residentTheme;
  if (!theme || typeof theme !== 'object') return null;
  return theme.key ?? null;
}

let currentBuildingId: number | undefined;

export function useResidentTheme(buildingId: number | undefined) {
  currentBuildingId = buildingId;
  const { data } = useApi<{ building?: { residentTheme?: ResidentThemeField } }>(
    buildingId ? `/api/v1/building/${buildingId}/config` : null,
  );

  useEffect(() => {
    // No data yet (still loading, offline, or the request failed): keep what the cold-start
    // preload painted. Only a real answer from the API changes the theme.
    if (!data) return;
    const key = readResidentThemeKey(data);
    if (key === undefined) return;
    applyResidentTheme(key);
  }, [data]);
}

/**
 * Re-read this tablet's resolved theme after a portal push (`display_settings { refetch }`).
 * Returns false when it could not (no building yet, network); the current look then stays.
 */
export async function refetchResidentTheme(): Promise<boolean> {
  if (!currentBuildingId) return false;
  try {
    const res = (await api.get(`/api/v1/building/${currentBuildingId}/config`)) as BuildingConfigBody;
    const key = readResidentThemeKey(res);
    if (key === undefined) return false;
    applyResidentTheme(key);
    return true;
  } catch {
    return false;
  }
}
